<!--
 * @Description: 申请开票。注意费用类型，供应商可申请综合服务费发票（返佣），其他类型的平台服务费由服务商发起（服务商不可申请综合服务费）。
                    ORDER_TYPE类型的发票流程：
                      综合服务费发票：
                        供应商申请 -> OSS开票 -> 供应商审核
                      其他服务费发票：
                        蓝票：服务商申请->供应商上传->服务商审核
                        红票：蓝通过后->服务商发起红冲->供应商上传红冲->服务商审核
                    委托代开：
                        ASP申请->OSS开票   
                    工业发票：
                        ASP开
                    个体发票：
                        OSS申请->OSS开   
                    项目收款发票：
                        ASP申请->供应商开->ASP审
                    服务费发票（商务/平台）：
                        ASP申请->OSS开->ASP审          
 * @Date: 2025-06-23 10:42:08
-->
<template>
  <BaseDialog
    width="950px"
    class="form-dialog form-dialog--label120 form-dialog-invoice-custom"
    title="申请开票"
    :model-value="true"
    :close-on-press-escape="true"
    :close-on-click-modal="false"
  >
    <BaseTextContent>
      <InvoiceOpenDialogInfo
        ref="infoRef"
        v-model="formData"
        :baseInfo="baseInfo"
        :disabled="isConfirm"
      />
      <InvoiceOpenDialogAddress v-model="addressList" />
      <InvoiceOpenDialogContent
        ref="contentRef"
        v-model="formData"
        :disabled="isConfirm"
      />
      <InvoiceOpenDialogDemand v-show="isConfirm" v-model="formData" />
      <InvoiceOpenDialogList
        :disabled="isConfirm"
        :contentRef="contentRef"
        v-model="formData"
      />
    </BaseTextContent>

    <template #footer>
      <BaseButton type="default" @click="onCancel">
        {{ isConfirm ? '上一步' : '取消' }}
      </BaseButton>
      <BaseButton @click="onConfirm">
        {{ isConfirm ? '确认提交' : '下一步' }}
      </BaseButton>
    </template>
  </BaseDialog>
</template>

<script lang="tsx" name="invoice-enterprise-dialog" setup>
import { withDefaults, defineProps, ref, onMounted, defineEmits } from 'vue';
import {
  BaseDialog,
  BaseTextContent,
  BaseButton,
  XrkMessage
} from 'xrk-components';
import {
  basicInformation,
  makeOutAnInvoice
} from '@V/invoice/src/data-source/apis';
import {
  BasicInformationData,
  MakeOutAnInvoiceParams,
  NotInvoicedListParams
} from '@V/invoice/src/data-source/types';
import InvoiceOpenDialogInfo from './invoice-open-dialog-info.vue';
import InvoiceOpenDialogAddress from './invoice-open-dialog-address.vue';
import InvoiceOpenDialogContent from './invoice-open-dialog-content.vue';
import InvoiceOpenDialogList from './invoice-open-dialog-list.vue';
import InvoiceOpenDialogDemand from './invoice-open-dialog-demand.vue';
import appStore from '@/store';
import { storeToRefs } from 'pinia';

type FormData = MakeOutAnInvoiceParams & NotInvoicedListParams;

const props = withDefaults(
  defineProps<{
    remove?: () => {};
  }>(),
  {
    remove() {
      return () => {
        console.log('remove');
      };
    }
  }
);
const emits = defineEmits<{
  (event: 'success', value: void): void;
}>();

const { info: userInfo } = storeToRefs(appStore.useUserStoreSetup);

const isConfirm = ref(false); // 确认禁止内容操作
const infoRef = ref();
const contentRef = ref();
const addressList = ref<BasicInformationData['addressList']>([]);
const baseInfo = ref<BasicInformationData['baseInfo']>({
  entpName: '',
  entpCertNo: '',
  phone: '',
  bankName: '',
  openBankNumber: '',
  companyBusinessAddress: '',
  demand: ''
});
// 提交数据与列表搜索数据放一起了
const formData = ref<FormData>({
  theSellerId: null,
  itemId: null,
  invoiceType: null,
  stateType: null,
  openOrderIds: [],
  addressId: null,
  userDefinedOrderType: null,
  auditSign: 1 // 写死
});

const onCancel = () => {
  if (isConfirm.value) {
    isConfirm.value = false;
  } else {
    props.remove();
  }
};
const onConfirm = async () => {
  if (!isConfirm.value) {
    try {
      await infoRef.value.validate();
    } catch (e) {
      XrkMessage.error('请到企业开票维护，完善企业开票信息，谢谢');
      return;
    }
    await Promise.all([infoRef.value.validate(), contentRef.value.validate()]);
    if (!formData.value.openOrderIds.length) {
      XrkMessage.error('请选择订单');
      return;
    }
    isConfirm.value = true;
  } else {
    await makeOutAnInvoice({
      ...formData.value,
      isBaseAsp: userInfo.value.isBaseAsp || 0
    });
    emits('success');
    props.remove();
  }
};
onMounted(async () => {
  const { data } = await basicInformation();
  addressList.value = data.addressList;
  baseInfo.value = data.baseInfo;
  formData.value.demand = baseInfo.value.demand;
  // 赋值开票地址(第一个)默认项
  formData.value.addressId =
    data.addressList.find(item => item.isDefault)?.id || null;
});
</script>

<style lang="scss">
.form-dialog-invoice-custom {
  .form-dialog__title {
    font-weight: 600;
    font-size: 16px;
    color: #1d2129;
    line-height: 24px;
    text-align: left;
    font-style: normal;
    text-transform: none;
    > h1 {
      text-align: left;
    }
  }
  .form-dialog__content {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0 16px;
  }
  .form-dialog__w280 {
    width: 100%;
  }
}
</style>
